// src/scenes/restaurant/Commandes/Commandes.jsx
//
// Supervision des commandes. La PRISE de commande se fait au point de vente
// (app caissier) : ici on suit, on consulte le détail, on encaisse une
// commande restée ouverte et on annule.
//
// Rappels du modèle :
//   - le modèle `Table` a été supprimé : une commande n'est plus rattachée à
//     une table, c'est une session de vente qui accumule des lignes
//   - `montant_recu` est facultatif à l'encaissement : omis, le serveur
//     encaisse le montant exact (pas de monnaie)
//   - `demander_addition` applique la remise et est rejouable tant que la
//     commande n'est pas payée
//   - une commande `sur compte` est reportée sur la note d'une chambre
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Card, CardContent, CircularProgress,
  FormControl, InputLabel, Select, InputAdornment,
  Tab, Tabs, Alert, Tooltip, Divider,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Search, Visibility, Cancel, Payments, Refresh,
  Warning, ReceiptLong, LocalOffer,
} from '@mui/icons-material';
import { commandeAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const STATUTS = {
  ouverte: { label: 'Ouverte', color: 'warning' },
  en_preparation: { label: 'En préparation', color: 'info' },
  servie: { label: 'Prête à payer', color: 'secondary' },
  payee: { label: 'Payée', color: 'success' },
  annulee: { label: 'Annulée', color: 'error' },
};

const TYPES = {
  sur_place: 'Sur place',
  emporter: 'À emporter',
  livraison: 'Livraison',
};

const METHODES = {
  especes: 'Espèces',
  carte: 'Carte',
  mobile: 'Mobile',
  compte: 'Sur compte (chambre)',
};

// Une commande non payée et non annulée est encore « en cours »
const EN_COURS = ['ouverte', 'en_preparation', 'servie'];

const fmt = (n) => new Intl.NumberFormat('fr-HT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(Number(n) || 0) + ' HTG';

const fmtDateHeure = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const versTableau = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const messageErreur = (error, defaut) => {
  const d = error.response?.data;
  if (!d) return error.message || defaut;
  if (typeof d === 'string') return d;
  if (d.error) return d.error;
  if (d.detail) return d.detail;
  if (Array.isArray(d.non_field_errors)) return d.non_field_errors[0];
  const premier = Object.entries(d)[0];
  if (premier) {
    const [champ, valeur] = premier;
    return `${champ} : ${Array.isArray(valeur) ? valeur[0] : valeur}`;
  }
  return defaut;
};

const CommandesPage = () => {
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('all');
  const [filtreType, setFiltreType] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [encaissement, setEncaissement] = useState(null); // { commande, remise }
  const [submitting, setSubmitting] = useState(false);

  const fetchCommandes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await commandeAPI.getAll();
      setCommandes(versTableau(response.data));
    } catch (err) {
      const msg = messageErreur(err, 'Erreur lors du chargement des commandes');
      setError(msg);
      toast.error(msg);
      setCommandes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCommandes(); }, [fetchCommandes]);

  const handleAnnuler = async (commande) => {
    const motif = window.prompt(
      `Annuler la commande ${commande.numero} ?\nMotif (optionnel) :`,
    );
    if (motif === null) return; // fenêtre fermée
    try {
      await commandeAPI.annuler(commande.id, motif || '');
      toast.success('Commande annulée');
      setDetail(null);
      fetchCommandes();
    } catch (err) {
      toast.error(messageErreur(err, "Erreur lors de l'annulation"));
    }
  };

  const handleEncaisser = async () => {
    const { commande, remise } = encaissement;
    setSubmitting(true);
    try {
      // La remise passe par `demander_addition`, qui recalcule les totaux.
      // Rejouable tant que la commande n'est pas payée.
      if (Number(remise) >= 0 && Number(remise) !== Number(commande.remise || 0)) {
        await commandeAPI.demanderAddition(commande.id, Number(remise) || 0);
      }
      // `montant_recu` omis → encaissement du montant exact
      await commandeAPI.payer(commande.id, { methode_paiement: 'especes' });
      toast.success('Commande encaissée');
      setEncaissement(null);
      setDetail(null);
      fetchCommandes();
    } catch (err) {
      toast.error(messageErreur(err, "Erreur lors de l'encaissement"));
    } finally {
      setSubmitting(false);
    }
  };

  const filtrees = useMemo(() => {
    const recherche = searchTerm.toLowerCase();
    return commandes.filter((c) => {
      const correspondRecherche =
        c.numero?.toLowerCase().includes(recherche) ||
        c.nom_client?.toLowerCase().includes(recherche) ||
        false;
      const correspondStatut = filtreStatut === 'all' || c.statut === filtreStatut;
      const correspondType = filtreType === 'all' || c.type_commande === filtreType;
      const correspondOnglet = activeTab === 0
        ? EN_COURS.includes(c.statut)
        : !EN_COURS.includes(c.statut);
      return correspondRecherche && correspondStatut && correspondType && correspondOnglet;
    });
  }, [commandes, searchTerm, filtreStatut, filtreType, activeTab]);

  const stats = useMemo(() => {
    const payees = commandes.filter((c) => c.statut === 'payee');
    const ca = payees.reduce((s, c) => s + (Number(c.montant_total) || 0), 0);
    return {
      enCours: commandes.filter((c) => EN_COURS.includes(c.statut)).length,
      payees: payees.length,
      ca,
      panierMoyen: payees.length ? ca / payees.length : 0,
      remises: payees.reduce((s, c) => s + (Number(c.remise) || 0), 0),
    };
  }, [commandes]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={<Button variant="outlined" size="small" onClick={fetchCommandes}>Réessayer</Button>}
        >
          <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  const totalApresRemise = encaissement
    ? Math.max(0, (Number(encaissement.commande.sous_total) || 0) - (Number(encaissement.remise) || 0))
    : 0;

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <ReceiptLong sx={{ mr: 2, verticalAlign: 'middle' }} />
              Commandes
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Suivi des ventes du restaurant — la prise de commande se fait au point de vente
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="N° de commande ou client..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ width: 300 }}
            />
            <Tooltip title="Rafraîchir">
              <IconButton onClick={fetchCommandes}><Refresh /></IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">En cours</Typography>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={stats.enCours > 0 ? 'warning.main' : 'text.primary'}
                >
                  {stats.enCours}
                </Typography>
                <Typography variant="caption" color="textSecondary">non encaissées</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Commandes payées</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">{stats.payees}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Panier moyen</Typography>
                <Typography variant="h4" fontWeight="bold">{fmt(stats.panierMoyen)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Remises accordées</Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">{fmt(stats.remises)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Statut</InputLabel>
            <Select value={filtreStatut} label="Statut" onChange={(e) => setFiltreStatut(e.target.value)}>
              <MenuItem value="all">Tous les statuts</MenuItem>
              {Object.entries(STATUTS).map(([cle, s]) => (
                <MenuItem key={cle} value={cle}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filtreType} label="Type" onChange={(e) => setFiltreType(e.target.value)}>
              <MenuItem value="all">Tous</MenuItem>
              {Object.entries(TYPES).map(([cle, label]) => (
                <MenuItem key={cle} value={cle}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '& .MuiTab-root': { minHeight: 48 } }}>
          <Tab label="En cours" />
          <Tab label="Historique" />
        </Tabs>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>N°</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Articles</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Remise</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Paiement</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {activeTab === 0 ? 'Aucune commande en cours' : 'Aucune commande dans l\'historique'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtrees.map((commande) => {
                  const statut = STATUTS[commande.statut] || { label: commande.statut, color: 'default' };
                  const enCours = EN_COURS.includes(commande.statut);
                  const remise = Number(commande.remise) || 0;
                  return (
                    <TableRow key={commande.id} hover>
                      <TableCell>
                        <Typography fontWeight="bold" variant="body2">{commande.numero}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{commande.nom_client || '—'}</Typography>
                        {commande.location_chambre && (
                          <Typography variant="caption" color="warning.main">sur note chambre</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={TYPES[commande.type_commande] || commande.type_commande}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{fmtDateHeure(commande.date_commande)}</Typography>
                      </TableCell>
                      <TableCell align="center">{commande.nombre_items ?? commande.lignes?.length ?? 0}</TableCell>
                      <TableCell align="right">
                        {remise > 0
                          ? <Typography color="warning.main" fontWeight="medium">− {fmt(remise)}</Typography>
                          : <Typography variant="body2" color="textSecondary">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{fmt(commande.montant_total)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {commande.statut === 'payee'
                            ? (METHODES[commande.methode_paiement] || commande.methode_paiement || '—')
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={statut.label} size="small" color={statut.color} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="Voir le détail">
                            <IconButton color="primary" size="small" onClick={() => setDetail(commande)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {enCours && (
                            <>
                              <Tooltip title="Encaisser">
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => setEncaissement({ commande, remise: commande.remise || 0 })}
                                >
                                  <Payments />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Annuler la commande">
                                <IconButton color="error" size="small" onClick={() => handleAnnuler(commande)}>
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Détail d'une commande */}
      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {detail && (
          <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ReceiptLong />
                  <Typography variant="h6">{detail.numero}</Typography>
                </Box>
                <Chip
                  label={(STATUTS[detail.statut] || {}).label || detail.statut}
                  size="small"
                  color={(STATUTS[detail.statut] || {}).color || 'default'}
                />
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography color="text.secondary" variant="body2">Client</Typography>
                <Typography variant="body2" fontWeight="medium">{detail.nom_client || '—'}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography color="text.secondary" variant="body2">Type</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {TYPES[detail.type_commande] || detail.type_commande}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography color="text.secondary" variant="body2">Date</Typography>
                <Typography variant="body2" fontWeight="medium">{fmtDateHeure(detail.date_commande)}</Typography>
              </Box>
              {detail.notes && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography color="text.secondary" variant="body2">Notes</Typography>
                  <Typography variant="body2">{detail.notes}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {!detail.lignes?.length ? (
                <Typography color="text.secondary" textAlign="center" py={2} variant="body2">
                  Aucun article sur cette commande
                </Typography>
              ) : (
                detail.lignes.map((ligne) => (
                  <Box key={ligne.id} display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
                    <Box minWidth={0}>
                      <Typography variant="body2">
                        {ligne.nom_item || ligne.plat_nom || ligne.boisson_nom} × {ligne.quantite}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmt(ligne.prix_unitaire)} l'unité · {ligne.type_item}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="medium">{fmt(ligne.total)}</Typography>
                  </Box>
                ))
              )}

              <Divider sx={{ my: 1.5 }} />

              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography color="text.secondary" variant="body2">Sous-total</Typography>
                <Typography variant="body2">{fmt(detail.sous_total)}</Typography>
              </Box>
              {Number(detail.remise) > 0 && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography color="text.secondary" variant="body2">Remise</Typography>
                  <Typography variant="body2" color="warning.main">− {fmt(detail.remise)}</Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" pt={1}>
                <Typography fontWeight="bold">Total</Typography>
                <Typography fontWeight="bold" sx={{ fontSize: '1.15rem' }}>{fmt(detail.montant_total)}</Typography>
              </Box>

              {detail.statut === 'payee' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Encaissée en {METHODES[detail.methode_paiement] || detail.methode_paiement}
                  {Number(detail.monnaie) > 0 && ` — monnaie rendue : ${fmt(detail.monnaie)}`}
                </Alert>
              )}
            </DialogContent>

            <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
              <Button onClick={() => setDetail(null)} variant="outlined">Fermer</Button>
              {EN_COURS.includes(detail.statut) && (
                <>
                  <Button color="error" onClick={() => handleAnnuler(detail)} startIcon={<Cancel />}>
                    Annuler
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Payments />}
                    onClick={() => setEncaissement({ commande: detail, remise: detail.remise || 0 })}
                  >
                    Encaisser
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Encaissement avec remise optionnelle */}
      <Dialog
        open={!!encaissement}
        onClose={() => !submitting && setEncaissement(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {encaissement && (
          <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Payments />
                <Typography variant="h6">Encaisser {encaissement.commande.numero}</Typography>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography color="text.secondary" variant="body2">Sous-total</Typography>
                <Typography variant="body2">{fmt(encaissement.commande.sous_total)}</Typography>
              </Box>

              <TextField
                label="Remise"
                type="number"
                fullWidth
                size="small"
                value={encaissement.remise}
                onChange={(e) => setEncaissement({ ...encaissement, remise: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LocalOffer fontSize="small" /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                  inputProps: { step: '0.01', min: '0' },
                }}
                error={Number(encaissement.remise) > Number(encaissement.commande.sous_total)}
                helperText={
                  Number(encaissement.remise) > Number(encaissement.commande.sous_total)
                    ? 'La remise dépasse le total'
                    : 'Optionnel — 0 pour aucune remise'
                }
                disabled={submitting}
                sx={{ mt: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h4" textAlign="center" fontWeight="bold">
                {fmt(totalApresRemise)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                Encaissement du montant exact, sans saisie de la somme remise.
              </Typography>
            </DialogContent>

            <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
              <Button onClick={() => !submitting && setEncaissement(null)} variant="outlined" disabled={submitting}>
                Annuler
              </Button>
              <Button
                onClick={handleEncaisser}
                variant="contained"
                color="success"
                disabled={submitting || Number(encaissement.remise) > Number(encaissement.commande.sous_total)}
                startIcon={submitting ? <CircularProgress size={20} /> : <Payments />}
              >
                Encaisser {fmt(totalApresRemise)}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CommandesPage;
